/**
 * One link between a logo and a customer org, as the customer edit screen's
 * Logos section sees it (flag REACT_APP_CUSTOMER_LOGOS_TAB_V2_ENABLED).
 *
 * The API (GET organizations/{orgId}/logos/attach) answers either the old
 * form - int[] of the manual marker ids - or, with its sub-flag
 * CustomerLogoLinkSourceEnabled on, one object per customer link with a
 * server-side source classification. Both forms are normalised into this
 * shape by mapCustomerLogoLinks, so the screens never see the wire form.
 */

/** Stable source codes of the API - data values, never translated as such. */
export type CustomerLogoLinkSource =
  | "manual"
  | "assigned"
  | "customerUpload"
  | "nav"
  | "derived"
  | "linked";

export const CUSTOMER_LOGO_LINK_SOURCES: readonly CustomerLogoLinkSource[] = [
  "manual",
  "assigned",
  "customerUpload",
  "nav",
  "derived",
  "linked",
];

/** Stable reason codes of a 409 { code: "notRemovable", reason } answer. */
export type CustomerLogoLinkNotRemovableReason =
  | "customerUpload"
  | "navMainCustomer"
  | "derivedChild";

export interface CustomerLogoLink {
  logoId: number;
  /**
   * manual = added from this section (marker), assigned = "Assign customer"
   * in the logo list (marker), nav = NAV's main customer for the logo,
   * derived = via the customer's parent (dealer group), customerUpload = the
   * customer's own upload, linked = source unknown (NAV's customer filter,
   * a historical assignment, ...).
   */
  source: CustomerLogoLinkSource;
  /** ISO UTC stamp of the marker (manual/assigned only), else null. */
  createdUtc?: string | null;
  /** "First Last" (fallback e-mail) of the marker's creator, else null. */
  createdByName?: string | null;
  /** True when DELETE .../attach/{logoId} is allowed for this link. */
  removable: boolean;
  /** Set when not removable: "customerUpload" | "navMainCustomer" | "derivedChild". */
  notRemovableReason?: CustomerLogoLinkNotRemovableReason | string | null;
}

/** A marker link - written by this section or by "Assign customer". */
export function isMarkerLink(link: CustomerLogoLink): boolean {
  return link.source === "manual" || link.source === "assigned";
}
